import React, { useState, useEffect } from 'react';
import { AdminAuthService } from '../services/AdminAuthService';
import './AdminLogin.css';

interface AdminLoginProps {
  onLoginSuccess: () => void;
}

export const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);

  const authService = AdminAuthService.getInstance();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isRateLimited && rateLimitSeconds > 0) {
      timer = setInterval(() => {
        const seconds = authService.getRateLimitResetSeconds();
        if (seconds <= 0) {
          setIsRateLimited(false);
          setRateLimitSeconds(0);
          setError('');
        } else {
          setRateLimitSeconds(seconds);
        }
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isRateLimited, rateLimitSeconds, authService]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await authService.login(username.trim(), password);
      if (result.success) {
        onLoginSuccess();
      } else {
        setError(result.error || 'Login failed.');
        if (authService.isRateLimited()) {
          setIsRateLimited(true);
          setRateLimitSeconds(authService.getRateLimitResetSeconds());
        }
      }
    } catch {
      setError('An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login__card">
        <div className="admin-login__header">
          <span className="admin-login__logo">🛡️</span>
          <h1 className="admin-login__title">PlayHub Admin</h1>
          <p className="admin-login__subtitle">Enter your admin credentials to continue</p>
        </div>

        <form className="admin-login__form" onSubmit={handleSubmit}>
          <div className="admin-login__field">
            <label className="admin-login__label" htmlFor="admin-username">
              Username
            </label>
            <input
              id="admin-username"
              className="admin-login__input"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="admin"
              autoComplete="username"
              disabled={isLoading || isRateLimited}
              autoFocus
            />
          </div>

          <div className="admin-login__field">
            <label className="admin-login__label" htmlFor="admin-password">
              Password
            </label>
            <input
              id="admin-password"
              className="admin-login__input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={isLoading || isRateLimited}
            />
          </div>

          {error && (
            <div className="admin-login__error" role="alert">
              {error}
            </div>
          )}

          {isRateLimited && (
            <div className="admin-login__rate-limit" role="alert">
              ⏳ Too many attempts. Try again in {rateLimitSeconds}s
            </div>
          )}

          <button
            className="admin-login__button"
            type="submit"
            disabled={isLoading || isRateLimited}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};
